import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/propgen_api";
import { TrendingUp, TrendingDown, Sparkles, ChevronRight } from "lucide-react";

const money = (n) => n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const hrs = (n) => n == null ? "—" : `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 })}h`;
const pct = (n) => n == null ? "—" : `${n > 0 ? "+" : ""}${n}%`;

export default function SimilarProjects({ projectId, limit = 3 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: res } = await api.get(`/projects/${projectId}/similar-projects`, { params: { limit } });
        if (mounted) setData(res);
      } catch { if (mounted) setData({ count: 0, projects: [] }); }
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, [projectId, limit]);

  if (loading) return null;
  if (!data || data.count === 0) {
    return (
      <div className="border bg-card p-4" data-testid="similar-projects-empty">
        <div className="eyebrow flex items-center gap-2"><Sparkles className="w-3 h-3 text-[var(--peak-blue)]" /> Similar past proposals</div>
        <div className="text-xs text-muted-foreground mt-2 italic">
          No delivered projects to compare against yet. Once you record actuals on completed proposals, similar-shape jobs surface here as a pricing sanity check.
        </div>
      </div>
    );
  }

  return (
    <div className="border bg-card" data-testid="similar-projects">
      <div className="px-4 py-2 border-b bg-muted/30 flex items-center gap-2">
        <div className="eyebrow flex items-center gap-2"><Sparkles className="w-3 h-3 text-[var(--peak-blue)]" /> Similar past proposals</div>
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground ml-auto">
          {data.count} match{data.count === 1 ? "" : "es"} · sanity-check before you send
        </span>
      </div>
      <div className="divide-y">
        {data.projects.map((p, i) => {
          const overBudget = (p.delta_pct ?? 0) > 0;
          const simPct = Math.round((p.similarity || 0) * 100);
          return (
            <Link
              key={p.id}
              to={`/superadmin/propgen/projects/${p.id}`}
              data-testid={`similar-project-${i}`}
              className="grid grid-cols-[80px_1fr_130px_130px_100px_20px] items-center px-4 py-3 gap-3 hover:bg-muted/40 transition-colors"
            >
              <div>
                <div className="font-display text-2xl font-black tracking-tighter text-[var(--peak-blue)] leading-none">{simPct}<span className="text-sm">%</span></div>
                <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground mt-0.5">match</div>
              </div>
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{p.name}</div>
                <div className="text-xs text-muted-foreground truncate">{p.client_name}</div>
                {p.shared_integrations?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {p.shared_integrations.slice(0, 3).map((s) => (
                      <span key={s} className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-[var(--peak-blue)] text-[var(--peak-blue)]">
                        {s}
                      </span>
                    ))}
                    {p.shared_feature_count > 0 && (
                      <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-neutral-400 text-muted-foreground">
                        +{p.shared_feature_count} shared features
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="font-mono text-sm font-bold">{money(p.estimate_external_price)}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">quoted price</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-sm">{p.actual_credits}<span className="text-muted-foreground text-xs">cr</span> / {hrs(p.actual_hours)}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">actual</div>
              </div>
              <div className={`text-right font-mono text-sm ${p.delta_pct == null ? "text-muted-foreground" : overBudget ? "text-red-700" : "text-emerald-700"}`}>
                <div className="flex items-center justify-end gap-1">
                  {p.delta_pct != null && (overBudget ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />)}
                  <span className="font-bold">{pct(p.delta_pct)}</span>
                </div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">vs forecast</div>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
